
import React from "react";
import { Organization, PaginationMeta } from "@/types";
import OrganizationCard from "./OrganizationCard";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { useLanguage } from "@/contexts/LanguageContext";

interface OrganizationListProps {
  organizations: Organization[];
  isLoading: boolean;
  meta?: PaginationMeta;
  currentPage: number;
  onPageChange: (page: number) => void;
}

const OrganizationList = ({
  organizations,
  isLoading,
  meta,
  currentPage,
  onPageChange,
}: OrganizationListProps) => {
  const { t, language } = useLanguage();
  
  // Display loading state
  if (isLoading) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500">{t("organizations.loading")}</p>
      </div>
    );
  }

  // No organizations found
  if (organizations.length === 0) {
    return (
      <div className="text-center py-16 text-gray-500">
        <p className="text-lg mb-2">{t("organizations.noOrganizations")}</p>
        <p className="text-sm">{t("common.noData")}</p>
      </div>
    );
  }

  // Get the total number of pages
  const totalPages = meta?.pages || 1;

  // Determine which page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    
    // Always show the current page
    pages.push(currentPage);
    
    // Add pages before the current page
    if (currentPage > 1) {
      if (currentPage > 2) {
        pages.unshift(currentPage - 1);
      }
      if (currentPage > 3) {
        pages.unshift(currentPage - 2);
      }
    }
    
    // Add pages after the current page
    if (currentPage < totalPages) {
      if (currentPage < totalPages - 1) {
        pages.push(currentPage + 1);
      }
      if (currentPage < totalPages - 2) {
        pages.push(currentPage + 2);
      }
    }
    
    // Sort the pages
    return [...new Set(pages)].sort((a, b) => a - b);
  };

  return (
    <div>
      {/* Organizations grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {organizations.map((organization, index) => (
          <OrganizationCard 
            key={organization.id} 
            organization={organization}
            index={index}
          />
        ))}
      </div>
      
      {/* Results count */}
      <div className="text-right text-sm text-gray-500 mt-2 mb-6">
        {language === 'de' 
          ? `Zeige ${organizations.length} von ${meta?.total || organizations.length} Organisationen`
          : `Showing ${organizations.length} of ${meta?.total || organizations.length} organizations`
        }
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination className="mt-8">
          <PaginationContent>
            {/* Previous page button */}
            {currentPage > 1 && (
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => onPageChange(currentPage - 1)}
                  className="cursor-pointer"
                />
              </PaginationItem>
            )}
            
            {/* First page */}
            {!getPageNumbers().includes(1) && (
              <>
                <PaginationItem>
                  <PaginationLink 
                    onClick={() => onPageChange(1)}
                    className="cursor-pointer"
                    isActive={currentPage === 1}
                  >
                    1
                  </PaginationLink>
                </PaginationItem>
                {getPageNumbers()[0] > 2 && (
                  <PaginationItem>
                    <span className="flex h-9 w-9 items-center justify-center">...</span>
                  </PaginationItem>
                )}
              </>
            )}
            
            {/* Page numbers */}
            {getPageNumbers().map((page) => (
              <PaginationItem key={page}>
                <PaginationLink
                  onClick={() => onPageChange(page)}
                  className="cursor-pointer"
                  isActive={currentPage === page}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}
            
            {/* Last page */}
            {!getPageNumbers().includes(totalPages) && totalPages > 1 && (
              <>
                {getPageNumbers()[getPageNumbers().length - 1] < totalPages - 1 && (
                  <PaginationItem>
                    <span className="flex h-9 w-9 items-center justify-center">...</span>
                  </PaginationItem>
                )}
                <PaginationItem>
                  <PaginationLink 
                    onClick={() => onPageChange(totalPages)}
                    className="cursor-pointer"
                    isActive={currentPage === totalPages}
                  >
                    {totalPages}
                  </PaginationLink>
                </PaginationItem>
              </>
            )}
            
            {/* Next page button */}
            {currentPage < totalPages && (
              <PaginationItem>
                <PaginationNext 
                  onClick={() => onPageChange(currentPage + 1)}
                  className="cursor-pointer"
                />
              </PaginationItem>
            )}
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
};

export default OrganizationList;
